import React          from 'react';
import AntdSelect     from 'antd/lib/select';
import Spin           from 'antd/lib/spin';
import useWillUnmount from '@widesk-core/hooks/useWillUnmount';
import '@widesk-ui/styles/Select.scss';

export type SelectOption = {
	disabled?: boolean;
	label: React.ReactNode;
} & ({
	value?: undefined;
	options: SelectOption[];
} | {
	value: unknown;
	options?: undefined;
})

export type SelectProps = Omit<FieldProps, 'onChange'> & {
	onChange?: ((value?: unknown) => void) | ((value?: unknown) => Promise<void>);
	options: SelectOption[];
	onOpen?: () => void;
	onUnmount?: () => void;
	onPopupScroll?: React.UIEventHandler<HTMLDivElement>;
	labelRender?: (props: {label: React.ReactNode, value: string | number}) => React.ReactNode;
	optionRender?: (props: {label: React.ReactNode, value: string | number | undefined}) => React.ReactNode;
	loading?: boolean;
	notFoundContent?: React.ReactNode;
	suffixIcon?: React.ReactNode;
	loadingBottom?: boolean;
	onSearch?: (search: string) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	onClear?: () => void;
	searchValue?: string;
	maxTagCount?: number | 'responsive';
	multiple?: boolean;
	allowClear?: boolean;
	showSearch?: boolean;
	optionFilterProp?: 'label' | 'value';
	status?: 'error' | 'warning';
	filterOption?: boolean;
	disableAutocomplete?: boolean;
	variant?: 'outlined' | 'filled' | 'borderless' | 'underlined' | 'inline';
	styles?: {
		root?: React.CSSProperties;
		popup?: React.CSSProperties;
	},
	showOnChangeSpinner?: boolean;
}

export const Select = React.forwardRef((props: SelectProps, ref) => {
	const {
		loadingBottom,
		onOpen,
		options,
		variant,
	} = props;

	useWillUnmount(() => props.onUnmount && props.onUnmount());
	const [onChangeLoading, setOnChangeLoading] = React.useState(false);

	const SelectElement = (
		<AntdSelect
			classNames={{
				root: variant ? `widesk-select widesk-select-${variant}` : 'widesk-select',
				popup: {
					root: variant ? `widesk-select-popup widesk-select-${variant}` : 'widesk-select-popup',
				},
			}}
			ref={ref as any}
			id={props.id ? `select-${props.id}` : undefined}
			size={props.size}
			autoFocus={props.autoFocus}
			onChange={async v => {
				setOnChangeLoading(true);
				await props.onChange?.(v);
				setOnChangeLoading(false);
			}}
			placeholder={props.placeholder}
			labelRender={props.labelRender}
			optionRender={props.optionRender
				? (oriOption => props.optionRender?.({ label: oriOption.label, value: oriOption.value }))
				: undefined
			}
			disabled={props.disabled}
			value={props.value}
			onClear={props.onClear}
			allowClear={props.allowClear}
			filterOption={props.filterOption || false}
			showSearch={props.showSearch || !!props.onSearch}
			onSearch={props.onSearch}
			searchValue={props.searchValue}
			onFocus={e => {
				if (props.disableAutocomplete) {
					e.target.setAttribute('autocomplete', 'disable');
				}
				if (props.onFocus) {
					props.onFocus();
				}
			}}
			onBlur={props.onBlur}
			loading={props.loading}
			notFoundContent={props.notFoundContent}
			suffixIcon={props.suffixIcon}
			status={props.status}
			onPopupScroll={props.onPopupScroll}
			maxTagCount={props.maxTagCount}
			mode={props.multiple ? 'multiple' : undefined}
			popupMatchSelectWidth={false}
			style={props.style}
			onOpenChange={open => {
				if (onOpen && open) {
					onOpen();
				}
			}}
			options={loadingBottom ? options.concat({
				label: undefined,
				value: <><Spin size="small" />&nbsp;&nbsp;Chargement...</>,
			}) : options}
			optionFilterProp={props.optionFilterProp}
			variant={variant === 'inline' ? 'borderless' : variant}
			styles={props.styles && {
				root: props.styles.root,
				popup: {
					root: props.styles.popup,
				}
			}}
		/>
	);
	return props.showOnChangeSpinner ? (
		<Spin spinning={onChangeLoading} size="small">
			{SelectElement}
		</Spin>
	) : SelectElement;
});

export default Select;
